import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { FORM_VALIDATION } from '../../constants/common';
import { DATE_PICKER_REGEX } from 'src/app/feature/customer-portal/constants/customer-portal.constant';
import * as _ from 'lodash';
import { INDEX } from '../../enum/shared.enum';

@Injectable({
  providedIn: 'root'
})
export class FormValidationService {
  formValidation = FORM_VALIDATION;

  constructor() { }

  /**
   * validateAlphabets() - method to validate if the control value contains only alphabetic characters or is empty.
   * If the field is empty or contains only whitespace, it returns a 'required' error.
   * @param control - The form control to validate.
   * @returns null if valid or a ValidationError object if invalid.
   */
  validateAlphabets(control: AbstractControl): ValidationErrors | null {
    const alphabetPattern = /^[A-Za-z.,'@/_-]+(\s[A-Za-z.,'@/_-]+)*$/;
    const value = control.value;
    if (!value || value.trim() === '') {
      return { required: true };
    }
    if (value && (value !== value.trim())) {
      return alphabetPattern.test(value) ? null : { invalidSpace: true };
    }
    if (/\s{2,}/.test(value)) {
      return alphabetPattern.test(value) ? null : { multipleSpaces: true };
    }
    return alphabetPattern.test(value) ? null : { invalidAlphabets: true };
  }
 /**
   * validateAlphabets() - method to validate if the control value contains only alphabetic characters or is empty.
   * If the field is empty or contains only whitespace, it returns a 'required' error.
   * @param control - The form control to validate.
   * @returns null if valid or a ValidationError object if invalid.
   */
 validateAlphabetsCreditApplication(control: AbstractControl): ValidationErrors | null {
  const alphabetPattern = /^[A-Za-z\s.,'@_\/-]*$/;
  const value = control.value;
  if (!value || value.trim() === '') {
    return { required: true };
  }
  if (value && (value !== value.trim())) {
    return alphabetPattern.test(value) ? null : { invalidSpace: true };
  }
  if (/\s{2,}/.test(value)) {
    return alphabetPattern.test(value) ? null : { multipleSpaces: true };
  }
  return alphabetPattern.test(value) ? null : { invalidAlphabets: true };
}
  /**
   * validateAlphanumericAndSpecialCharacters() - method to validate if the control value contains alphanumeric and special characters or is empty.
   * @param control - The form control to validate.
   * @returns null if valid or a ValidationError object if invalid.
   */
  validateAlphanumericAndSpecialCharacters(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const regex = /^[A-Za-z0-9 !"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]+$/; 
    // If the value is empty or just spaces, return the required error
    if (!value || value.trim() === '') {
      return { required: true };
    }
    if (value && !regex.test(value)) {
      return { invalidAlphanumericSpecial: true }; // Validation key for error message
    }
    return null;
  }

  /**
   * validateAlphanumericSpecialChars() - method to validate if the control value contains alphanumeric characters, spaces, and special characters.
   * @param control - The form control to validate.
   * @returns 
   */
  validateAlphanumericSpecialChars(control: AbstractControl): ValidationErrors | null {
    const pattern = /^[A-Za-z0-9!@#$%^&*()_+\-=\[\]{};:'",.<>\/?\\|`~\s]*$/;
    const value = control.value;
    // If the value is empty or just spaces, return the required error
    if (!value || value.trim() === '') {
      return { required: true };
    }
     // Check for leading or trailing spaces
    if (control.value !== control.value.trim()) {
      return { invalidSpace: true }; // Leading or trailing spaces
    }
    return pattern.test(value) ? null : { invalidName: true };
  }

  /**
   * validateAlphanumeric() - method to validate if the control value contains only alphanumeric characters.
   * @param control - The form control to validate.
   * @returns null if valid or a ValidationError object if invalid.
   */
  validateAlphanumeric(control: AbstractControl): ValidationErrors | null {
    const pattern = /^[A-Za-z0-9]*$/; // Only allows alphanumeric characters (letters and digits)
    const alphaNumeric = FORM_VALIDATION.alphaNumericValidation;
    const value = control.value;
    // If the value is empty or contains only whitespace, return the 'required' error
    if (!value || value.trim() === '') {
      return { required: true };
    }
    // If the value does not match the alphanumeric pattern, return the 'invalidChars' error
    return pattern.test(value) ? null : { invalidChars: true };
  }

  /**
   * validateBusinessType() - method to validate if the field is alphanumeric 
   * @param control - the form control to valdiate
   * @returns - the validation message
   */
  validateBusinessType(control: AbstractControl): ValidationErrors | null {
    const pattern = /^[A-Za-z0-9\s]*$/; // Allows alphanumeric characters and spaces
    const value = control.value;
    // If the value is empty or contains only whitespace, return the 'required' error
    if (!value || value.trim() === '') {
      return { required: true };
    }
    // If the value does not match the alphanumeric pattern, return the 'invalidChars' error
    return pattern.test(value) ? null : { invalidChars: true };
  }

  /**
   * validateNo() - method to validate if the control value contains only numbers.
   * @param control - The form control to validate.
   * @returns null if valid or a ValidationError object if invalid.
   */
  validateNo(control: AbstractControl): ValidationErrors | null {
    const phonePattern = /^[0-9]*$/; // Only numbers allowed 
    const value = control.value;
     if (!value || (value ?? '').toString().trim() === '') {
      return { required: true }; // Return required error if empty
    }
    return phonePattern.test(value) ? null : { invalidno: true }; // If it contains anything other than numbers
  }

  /**
   * validateNoLoc() - validations for form controls
   * @param control - control name
   * @returns - an error message
   */
  validateNoLoc(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === null || value === undefined) {
      return { required: true };
    }
    if (typeof value !== 'string') {
      return { invalidno: true };
    }
    if (!value || value.trim() === '' || (value.trim() === '$')) {
      return { required: true };
    }
    const cleanValue = value.replace(/[$,.]/g, '');
    const phonePattern = /^\d{1,17}$/;

    if (cleanValue?.length > 17) {
      return { maxlength: true };
    }
    return phonePattern.test(cleanValue) ? null : { invalidno: true };
  }

  /**
   * validatePhoneNo() - method to validate phone numbers
   * @param control - the value entered
   * @returns - the appropriate error messages
   */
  validatePhoneNo(control: AbstractControl): ValidationErrors | null {
    const phonePattern = FORM_VALIDATION.phoneNovalidation;
    const value = control.value;
    if (value && !phonePattern.test(value)) {
      return { invalidPhone: true }; // Invalid phone number if it contains non-permitted characters
    }
    if (value?.length > 20) {
      return { maxLength: true };
    }
    if (/[a-zA-Z]/.test(value)) {
      return { invalidPhone: true }; // Return error if alphabetic characters are found
    }
    return null;
  }

  /**
   * validateOptionalPhoneNo() - method to validate the optional phone numbers
   * @param control - the value entered
   * @returns - the appropriate error message if any
   */
  validateOptionalPhoneNo(control: AbstractControl): ValidationErrors | null {
    const phonePattern = FORM_VALIDATION.phoneNovalidation;
    const value = control.value;
    if (value) {
      if (!phonePattern.test(value)) {
        return { invalidPhone: true }; // Invalid phone number if it contains non-permitted characters
      }
      if (value.length < 10) {
        return { minLength: true }; // Minimum length validation
      }
      if (value.length > 20) {
        return { maxLength: true };
      }
      if (/[a-zA-Z]/.test(value)) {
        return { invalidPhone: true }; // Return error if alphabetic characters are found
      }
    }
    return null;
  }

  /**
   * validateTrPhoneNo() - method to validate the phone numbers for trade references in customer credit application
   * @param control - the value entered
   * @returns - the appropriate error message if any
   */
  validateTrPhoneNo(control: AbstractControl): ValidationErrors | null {
    const phonePattern = FORM_VALIDATION.phoneNovalidation;
    const value = control.value;
    if (!value) {
      return { required: true }; // Required field validation
    }
    if (!phonePattern.test(value)) {
      return { invalidPhone: true }; // Invalid phone number if it contains non-permitted characters
    }
    if (value.length < 10) {
      return { minLength: true }; // Minimum length validation
    }
    if (value.length > 20) {
      return { maxLength: true };
    }
    if (/[a-zA-Z]/.test(value)) {
      return { invalidPhone: true }; // Return error if alphabetic characters are found
    }
    return null;
  }

  /**
   * validateTrPhoneNo() - method to validate the phone numbers for trade references in customer credit application
   * @param control - the value entered
   * @returns - the appropriate error message if any
   */
  validateBankPhoneNo(control: AbstractControl): ValidationErrors | null {
    const phonePattern = FORM_VALIDATION.phoneNovalidation;
    const value = control.value;
     if (!value || value.trim() === '') {
      return { required: true }; // Return required error if empty
    }
    if (!phonePattern.test(value)) {
      return { invalidPhone: true }; // Invalid phone number if it contains non-permitted characters
    }
    if (value.length < 10) {
      return { minLength: true }; // Minimum length validation
    }
    if (value.length > 20) {
      return { maxLength: true };
    }
    if (/[a-zA-Z]/.test(value)) {
      return { invalidPhone: true }; // Return error if alphabetic characters are found
    }
    return null;
  }


  validateFederalTaxTin(control: AbstractControl): ValidationErrors | null {
    // const tinPattern = /^[0-9-]+$/; // Only digits and hyphens are allowed
    const tinPattern = FORM_VALIDATION.tinValidation;
    const value = control.value;
    if (!value || value.trim() === '') {
      return { required: true }; // Return required error if empty
    }
    if (value.length > 15) {
      return { maxLength: true };
    }
    return tinPattern.test(value) ? null : { invalidTin: true };
  }

  validateCurrencyFormat(control: AbstractControl): ValidationErrors | null {
    const salePattern = /^\$(\d{1,3})(?:,(\d{3}))*(\.\d{2})?$/; // Dollar, numbers with commas, and optional decimal   
    const value = control.value;
    // If value is empty, return no error (you can change this if the field is required)
    if (!value || value.trim() === '') {
      return null;
    }
    // Test if the value matches the required pattern
    return salePattern.test(value) ? null : { invalidCurrency: true };
  }

  /**
   * validateEmail() - method to validate if the control value is a valid email format.
   * If the field is empty or contains only whitespace, it returns a 'required' error.
   * @param control - The form control to validate.
   * @returns null if valid or a ValidationError object if invalid.
   */
  validateEmail(control: AbstractControl): ValidationErrors | null {
    // const emailPattern = /^[^\s][a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}[^\s]$/;
    const emailPattern = FORM_VALIDATION.emailValidation;
    const trimmedValue = control.value;
    if (!trimmedValue || trimmedValue === '') {
      return { required: true };  // Empty or whitespace only input
    }
    if (trimmedValue && (trimmedValue !== trimmedValue.trim())) {
      return emailPattern.test(trimmedValue) ? null : { invalidSpace: true };
    }
    if (trimmedValue?.length > 80) {
      return { maxLength: true };
    }
    return emailPattern.test(trimmedValue) ? null : { invalidEmail: true };
  }

  /**
   * validateJobTitle() - method to validate if the control value is a valid job title format.
   * Allows alphanumeric characters, spaces, and specific special characters.
   * If the field is empty or contains only whitespace, it returns a 'required' error.
   * @param control - The form control to validate.
   * @returns null if valid or a ValidationError object if invalid.
   */
  validateJobTitle(control: AbstractControl): ValidationErrors | null {
    const jobTitlePattern = /^(?! )(?!.* {2})(?!.* $)[A-Za-z0-9 !"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]+$/;
    const specialCharacters = /[A-Za-z0-9]/;
    const trimmedValue = control.value;
    if (trimmedValue?.trim() === '') {
      return { required: true };
    }
    if (trimmedValue && (trimmedValue !== trimmedValue.trim())) {
      return jobTitlePattern.test(trimmedValue) ? null : { invalidSpace: true };
    }
    if (/\s{2,}/.test(trimmedValue)) {
      return jobTitlePattern.test(trimmedValue) ? null : { multipleSpaces: true };
    }
    // Must contain at least one alphanumeric character
    if (!specialCharacters.test(trimmedValue)) {
      return { specialCharactersOnly: true };
    }
    return jobTitlePattern.test(trimmedValue) ? null : { invalidJobTitle: true };
  }

  /**
  * validateJobTitleAddUser() - method to validate if the control value is a valid job title format.
  * Allows alphanumeric characters, spaces, and specific special characters and it will not allow trailing,leading spaces.
  * If the field is empty or contains only whitespace, it returns a 'required' error.
  * @param control - The form control to validate.
  * @returns null if valid or a ValidationError object if invalid.
  */
  validateJobTitleAddUser(control: AbstractControl): ValidationErrors | null {
    const jobTitlePattern = /^[A-Za-z0-9]+(?:[\s*()":{}|<>&-]+[A-Za-z0-9]+)*$/;
    const trimmedValue = control.value;  // Ensure null values are handled
    if (trimmedValue === '') {
      return { required: true };  // Empty or whitespace only input
    }
    if (trimmedValue && (trimmedValue !== trimmedValue.trim())) {
      return jobTitlePattern.test(trimmedValue) ? null : { invalidSpace: true };
    }
    if (/\s{2,}/.test(trimmedValue)) {
      return jobTitlePattern.test(trimmedValue) ? null : { multipleSpaces: true };
    }
    return jobTitlePattern.test(trimmedValue) ? null : { invalidJobTitle: true };
  }

  /**
   * validatePhone() - method to validate if the control value is a valid phone number format.
   * Allows numbers, dashes, plus, and parentheses.
   * If the field is empty or contains only whitespace, it returns a 'required' error.
   * @param control - The form control to validate.
   * @returns null if valid or a ValidationError object if invalid.
   */
  validatePhone(control: AbstractControl): ValidationErrors | null {
    const phonePattern = /^[+]*[0-9]*[()\d-]*$/;
    const value = control.value || '';
    const trimmedValue = value.trim();
    const minLength = 10;
    if (trimmedValue === '') {
      return { required: true };
    }
    const digitsOnly = trimmedValue.replace(/\D/g, '');
    if (digitsOnly.length < minLength) {
      return { minLength: true };
    }
    return phonePattern.test(trimmedValue) ? null : { invalidPhone: true };
  }


  /**
   * validateAtLeastOneSelected() - method to validate if at least one checkbox from a list of controls is selected.
   * Returns an error if none of the checkboxes are selected.
   * @param controls - An array of form controls representing checkboxes.
   * @returns null if at least one checkbox is selected, otherwise a ValidationError object.
   */
  validateAtLeastOneSelected(controls: AbstractControl[]): ValidationErrors | null {
    const isAnySelected = controls.some(control => control.value == true);
    return isAnySelected ? null : { atLeastOneRequired: true };
  }

  /**
   * phoneValidator() - This functions evaluated the phone  number field and throws erros according to that.
   * @returns - used to validate phone number in profile page;
   */
  phoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value || value.trim() === '') {
        return null;
      }
      const phonePattern = this.formValidation.phoneValidation;
      const minLengthValid = value.replace(/[\s()-]/g, '').length >= 10;
      const validPattern = phonePattern.test(value);

      if (!minLengthValid) {
        return { minLengthError: true };
      }
      if (!validPattern) {
        return { invalidPhone: true };
      }
      return null;
    };
  }


  /**
   * JobTitleValidator() - used to validate a job title
   * @param control - The form control to validate.
   * @returns ValidationErrors | null - Returns an error object if validation fails, or null if it passes.
   */
  JobTitleValidator(control: AbstractControl): ValidationErrors | null {
    const trimmedValue = control.value?.trim();

    if (!trimmedValue) {
      return { required: true };
    }

    if (/\s{2,}/.test(trimmedValue)) {
      return { invalidSpace: true };
    }

    if (!/[a-zA-Z0-9]/.test(trimmedValue)) {
      return { invalidJobTitle: true };
    }

    return null;
  }

  /**
   * streetValidator() - method to validate the street field.
   * Allows alphanumeric characters, special characters, and spaces.
   * Limits the input to a maximum of 85 characters.
   * If the field is empty, returns a 'required' error.
   * @param control - The form control to validate.
   * @returns null if valid or a ValidationError object if invalid.
   */
  streetValidator(control: AbstractControl): ValidationErrors | null {
    const value = control?.value?.trim() || '';
    const streetPattern = FORM_VALIDATION.streetValidation;

    if (!value) {
      return { required: true };
    }
    if (value.length > 85) {
      return { maxLength: true };
    }
    return streetPattern.test(value) ? null : { invalidStreet: true };
  }

  /**
  * cityValidator() - method to validate the city field.
  * Allows alphanumeric characters and spaces.
  * Limits the input to a maximum of 50 characters.
  * If the field is empty, returns a 'required' error.
  * @param control - The form control to validate.
  * @returns null if valid or a ValidationError object if invalid.
  */
  cityValidator(control: AbstractControl): ValidationErrors | null {
    const value = control?.value?.trim() || '';
    const cityPattern = FORM_VALIDATION.cityValidation;

    if (!value) {
      return { required: true };
    }
    if (value.length > 50) {
      return { maxLength: true };
    }
    return cityPattern.test(value) ? null : { invalidCity: true };
  }

  /**
   * zipCodeValidator() - Method to validate the ZIP code
   * @param control - The form control to validate
   * @returns null if valid or a ValidationError object if invalid.
   */
  zipCodeValidator(control: AbstractControl): ValidationErrors | null {
    const value = control?.value?.trim() || '';
    const zipCodePattern = /^[0-9]{1,12}$/;

    if (!value) {
      return { required: true };
    }
    if (value.length > 5) {
      return { maxLength: true };
    }
    return zipCodePattern.test(value) ? null : { invalidZipCode: true };
  }

  /**
   * dateValidator() - Method to validate the date field.
   * @param control - The form control to validate
   * @returns null if valid or a ValidationError object if invalid.
   */
  dateValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const date = new Date(control.value);
    const year = date.getFullYear();
    if (year >= 1 && year <= 9999) {
      return null;
    } else {
      return { invalidDate: true };
    }
  }

  /**
   * validateNoWithComma() - Method to validate a phone number that allows numbers and commas.
   * @param control - The form control to validate
   * @returns 
   */
  validateNoWithComma(control: AbstractControl): ValidationErrors | null {
    const phonePattern = /^[0-9,]*$/; // Only numbers and commas allowed
    const maxLength = 9;
    const value = control.value;
    if (value) {
      if (!phonePattern.test(value)) {
        return { invalidno: true };
      }
      // Remove commas for the length validation
      const numericValue = value.replace(/,/g, '');
      if (numericValue.length > maxLength) {
        return { maxlength: true };
      }
    }
    return null;
  }

  /**
   * validateProjectName() - Method to validate the Project Name
   * @param control - The form control to validate
   * @returns null if valid or a ValidationError object if invalid.
   */
  validateProjectName(control: AbstractControl): ValidationErrors | null {
    const jobTitlePattern = FORM_VALIDATION.projectName;  //Allow alphanumeric special characters
    // Ensure control.value is not null or undefined
    const trimmedValue = control.value ? control.value.trim() : '';
    // Check if the value is empty
    if (trimmedValue === '') {
      return { required: true };  // Empty or whitespace-only input
    }
    // Check for leading or trailing spaces
    if (control.value !== control.value.trim()) {
      return { invalidSpace: true }; // Leading or trailing spaces
    }
    // Check for multiple consecutive spaces
    if (/\s{2,}/.test(trimmedValue)) {
      return { multipleSpaces: true }; // Multiple consecutive spaces
    }
    return null;
  }


  /**
   * dateRangeValidator() - Method to validate the date field.
   * @param control - The form control to validate
   * @returns null if valid or a ValidationError object if invalid.
   */
  dateRangeValidator(dateFormat): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const startDateControl = formGroup.get('startDate');
      const endDateControl = formGroup.get('endDate');
      let startDate = startDateControl?.value;
      let endDate = endDateControl?.value;
      if (dateFormat == 2) {
        const parseDate = (dateStr: string): string => {
          const [day, month, year] = _.map(_.split(dateStr, '-'), Number);
          return `${year}-${month - 1}-${day}`;  // month is zero-based in JavaScript Date
        };
        startDate = parseDate(startDate);
        if (endDate)
          endDate = parseDate(endDate);
      }
      if (startDate && endDate) {
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);
        if (startDateObj > endDateObj) {
          return { invalidRange: { from: startDate, to: endDate } };
        }
      }
      return null;
    }
  }

  /**
   * streetValidator() - method to validate the street field.
   * Allows alphanumeric characters, special characters, and spaces.
   * Limits the input to a maximum of 85 characters.
   * If the field is empty, returns a 'required' error.
   * @param control - The form control to validate.
   * @returns null if valid or a ValidationError object if invalid.
   */
  streetValidatorWithSpecialChar(control: AbstractControl): ValidationErrors | null {
    const value = control?.value?.trim() || '';
    // const streetPattern = /^[a-zA-Z0-9@#?!&.,\-()'"/:;+\s]{1,85}$/;
    const streetPattern = FORM_VALIDATION.streetValidationSpecialChar;

    if (!value) {
      return { required: true };
    }
    if (value?.length > 85) {
      return { maxLength: true };
    }
    return streetPattern.test(value) ? null : { invalidStreet: true };
  }

  /**
   * dateValidatorNew() - to get validation messages
   * @param preferredDateFormat - preferred date format from user
   * @returns - validator with true or false
   */
  dateValidatorNew(preferredDateFormat: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const dateString = control.value;

      if (!dateString) {
        return null;
      }

      const normalizedDateString = _.replace(dateString, DATE_PICKER_REGEX, '-');
      const parts = _.split(normalizedDateString, '-');
      if (parts?.length !== 3) {
        return { invalidDate: true };
      }

      let day, month, year;

      if (preferredDateFormat === INDEX.one) {
        [month, day, year] = parts;
      } else {
        [day, month, year] = parts;
      }

      if (year.length !== 4) {
        return { invalidDate: true };
      }

      const dayNum = Number(day);
      const monthNum = Number(month);
      const yearNum = Number(year);

      if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
        return { invalidDate: true };
      }

      const daysInMonth = this.getDaysInMonth(monthNum, yearNum);
      if (dayNum > daysInMonth) {
        return { invalidDate: true };
      }

      return null;
    };
  }

  /**
   * getDaysInMonth() - Helper method to get the number of days in a month.
   * @param month - month number (1-12)
   * @param year - year number
   * @returns - number of days in the month
   */
  private getDaysInMonth(month: number, year: number): number {
    if (month === 2) {
      return (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) ? 29 : 28;
    }

    if ([4, 6, 9, 11].includes(month)) {
      return 30;
    }

    // All other months have 31 days
    return 31;
  }

  /**
   * dateValidatorNew() - to get validation messages
   * @param preferredDateFormat - preferred date format from user
   * @returns - validator with true or false
   */
  dateValidatorMultiple(preferredDateFormat: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const dateString = control.value;

      if (!dateString) {
        return null;
      }

      const normalizedDateString = _.replace(dateString, DATE_PICKER_REGEX, '-');
      const parts = _.split(normalizedDateString, '-');

      // Check if the parts array has the expected length
      if (parts?.length !== 3) {
        return { invalidDate: true };
      }

      let day, month, year;

      if (preferredDateFormat === INDEX.one) {
        [month, day, year] = parts;
      } else {
        [day, month, year] = parts;
      }

      if (year.length !== 4) {
        return { invalidDate: true };
      }

      const dayNum = Number(day);
      const monthNum = Number(month);
      const yearNum = Number(year);

      if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
        return { invalidDate: true };
      }

      const daysInMonth = this.getDaysInMonth(monthNum, yearNum);
      if (dayNum > daysInMonth) {
        return { invalidDate: true };
      }

      return null;
    };
  }

  /**
   * dateValidatorDelegate() - to get validation messages if the user types past dates
   * @param preferredDateFormat - preferred date format from user
   * @returns - validator with true or false
   */

  dateValidatorDelegate(preferredDateFormat: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const dateString = control.value;

      if (!dateString) {
        return null;
      }
      const normalizedDateString = _.replace(dateString, DATE_PICKER_REGEX, '-');
      const parts = _.split(normalizedDateString, '-');

      // Check if the parts array has the expected length
      if (parts?.length !== 3) {
        return { invalidDate: true };
      }

      let day, month, year;

      if (preferredDateFormat === INDEX.one) {
        [month, day, year] = parts;
      } else {
        [day, month, year] = parts;
      }
      if (year.length > INDEX.four || year.length < INDEX.four) {
        return { invalidDate: true };
      }
      const date = new Date(year, month - 1, day);
      const isValidDate = date && date.getFullYear() === Number(year) && date.getMonth() + 1 === Number(month) && date.getDate() === Number(day);

      if (!isValidDate) {
        return { invalidDate: true };
      }

      // Compare with today's date
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Strip time for accurate comparison
      date.setHours(0, 0, 0, 0);  // Same here

      if (date < today) {
        return { pastDate: true }; // Return error if the date is in the past
      }

      return null; // No error
    };
  }

  /**
   * dateValidatorNew() - to get validation messages
   * @param preferredDateFormat - preferred date format from user
   * @returns - validator with true or false
   */
  dateValidatorCompanyProfile(preferredDateFormat: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const dateString = control.value;

      if (!dateString) {
        return null;
      }
      const normalizedDateString = _.replace(dateString, DATE_PICKER_REGEX, '-');
      const parts = _.split(normalizedDateString, '-');

      // Check if the parts array has the expected length
      if (parts?.length !== 3) {
        return { invalidDate: true };
      }

      let day, month, year;

      if (preferredDateFormat === INDEX.one) {
        [month, day, year] = parts;
      } else {
        [day, month, year] = parts;
      }
      if (year.length > INDEX.four || year.length < INDEX.four) {
        return { invalidDate: true };
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to midnight

      const date = new Date(`${year}-${month}-${day}`);
      const isValidDate = date && date.getFullYear() === Number(year) && date.getMonth() + 1 === Number(month) && date.getDate() === Number(day);

      if (date > today) {
        return { invalidDate: true }; // Date is greater than today
      }
      return isValidDate ? null : { invalidDate: true };
    };
  }

  /**
   * noZeroValidator() - Custom validator to prevent '0', '00', '0000' etc.
   */
  noZeroValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      // Check if the value is '0' or starts with multiple zeros like '00', '0000', etc.
      if (value === '0' || /^0+$/g.test(value)) {
        return { noZero: true }; // Return an error object if the value is invalid
      }
      return null; // Return null if the value is valid
    };
  }

  /**
   * validateYear() - Method to validate the year in 4 digits and between 1900 to current year
   * @param control - The form control to validate.
   * @returns - null if valid or a ValidationError object if invalid
   */
  validateYear(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  if (!value) return null;

  const yearPattern = /^[0-9]{4}$/; // 4 digits validation
  const currentYear = new Date().getFullYear();

  if (!yearPattern.test(value)) {
    return { invalidno: true }; // Not a 4-digit number and anything other than numbers
  }

  const year = Number(value);
  if (year < 1900 || year > currentYear) {
    return { invalidYear: true }; // Out of valid range
  }

  return null;
}

}
